import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '~/chords/lib/cn';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';
import type { InputRounded, InputSize, InputState, InputType } from './types';
import { inputVariants } from './variants';

export interface InputProps
  extends ComponentProps<Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>> {
  type?: InputType;
  size?: InputSize;
  rounded?: InputRounded;
  value?: string | number;
  state?: keyof typeof InputState;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { type, size = 'large', rounded = 'xsmall', state = 'default', ...props },
    ref,
  ) => {
    const stateClasses = inputVariants({
      state,
      rounded,
      size,
    });

    const className = getClassNameOverride(props, 'Input');
    const {
      dangerouslyOverrideClassName: _dangerouslyOverrideClassName,
      ...propsWithoutClassName
    } = props;

    return (
      <input
        aria-invalid={state === 'error'}
        className={cn(stateClasses, className)}
        type={type}
        ref={ref}
        {...propsWithoutClassName}
      />
    );
  },
);

Input.displayName = 'Input';
